import React, { useCallback, useState } from "react";
import DadosEstadoCivilDefaultForm from "./DadosEstadoCivilDefaultForm";
import { useDispatch, useSelector } from "react-redux";
import {
  getPreenchimentoOperacao,
  isGravandoPreenchimentoOperacao,
} from "../../../../selectors/operacao.selectors";
import useCompleteForm from "../../../../hooks/useCompleteForm";
import validators from "../../../../utils/validators";
import { actions } from "../../../../actions/operacao.actions";
import {
  removeNonDigitsFromString,
  transformToDate,
} from "../../../../utils/basic";
import formatters from "../../../../utils/formatters";

const DadosEstadoCivilDefaultFormConnected = ({
  handleGoTo0,
  events,
  comprador,
  setComprador,
  ...props
}) => {
  const dispatch = useDispatch();
  const {
    operacao,
    dominios: { estadoCivil: estadosCivis, nacionalidade: nacionalidades },
  } = useSelector(getPreenchimentoOperacao);
  const submitting = useSelector(isGravandoPreenchimentoOperacao);

  const { conjuge, uniaoEstavel } =
    estadosCivis.find((e) => e.id === comprador?.estadoCivil?.id) ?? {};
  const [exibeConjuge, setExibeConjuge] = useState(
    (comprador?.uniaoEstavel || conjuge) ?? false
  );
  const [exibeUniaoEstavel, setExibeUniaoEstavel] = useState(
    uniaoEstavel ?? false
  );

  const isSegundoCompradorConjuge =
    !comprador.principal &&
    !!operacao.participantes.find((p) => p.principal)?.conjuge?.compoeRenda;

  const callback = useCallback(
    (data) => {
      const c = data.operacao.participantes.find((p) => p.id === comprador.id);
      if (!!c) {
        setComprador({
          ...c,
          fluxo: data.validacao[
            data.operacao.detalhe.correntista ? "correntista" : "completa"
          ]?.participantes?.find((f) => f.id === c?.id)?.etapas,
        });
      }
      handleGoTo0();
    },
    [handleGoTo0, setComprador, comprador]
  );

  const [formProps, handleSubmit] = useCompleteForm({
    rules: (form) => ({
      estadoCivil: validators.object({ required: true }),
      conjuge: {
        nome: validators.string({
          custom: {
            requiredIf: (value) => {
              const uniaoEstavel = form.getValues("uniaoEstavel");

              if (
                (!exibeConjuge && !uniaoEstavel) ||
                isSegundoCompradorConjuge
              ) {
                return true;
              }

              if (value !== undefined && value !== null && value !== "")
                return true;
              return "Campo obrigatório";
            },
            nomeCompleto: (value) =>
              !value ||
              (value.indexOf(" ") > 0 &&
                value.substring(value.indexOf(" ") + 1).length > 0) ||
              "Nome informado deve ser completo",
          },
        }),
        cpfCnpj: validators.cpf({
          custom: {
            requiredIf: (value) => {
              const uniaoEstavel = form.getValues("uniaoEstavel");

              if (
                (!exibeConjuge && !uniaoEstavel) ||
                isSegundoCompradorConjuge
              ) {
                return true;
              }

              if (value !== undefined && value !== null && value !== "")
                return true;
              return "Campo obrigatório";
            },
          },
        }),
        dataNascimento: validators.date({
          min: "01/01/1900",
          max: formatters.dates.short(new Date()),
          custom: {
            requiredIf: (value) => {
              const uniaoEstavel = form.getValues("uniaoEstavel");

              if (
                (!exibeConjuge && !uniaoEstavel) ||
                isSegundoCompradorConjuge
              ) {
                return true;
              }

              if (value !== undefined && value !== null && value !== "")
                return true;
              return "Campo obrigatório";
            },
          },
        }),
        telefone: validators.phone({
          custom: {
            requiredIf: (value) => {
              const uniaoEstavel = form.getValues("uniaoEstavel");

              if (
                (!exibeConjuge && !uniaoEstavel) ||
                isSegundoCompradorConjuge
              ) {
                return true;
              }

              if (value !== undefined && value !== null && value !== "")
                return true;
              return "Campo obrigatório";
            },
          },
        }),
        nacionalidade: validators.object({
          custom: {
            requiredIf: (value) => {
              const uniaoEstavel = form.getValues("uniaoEstavel");

              if (
                (!exibeConjuge && !uniaoEstavel) ||
                isSegundoCompradorConjuge
              ) {
                return true;
              }

              if (value !== undefined && value !== null && value !== "")
                return true;
              return "Campo obrigatório";
            },
          },
        }),
      },
    }),
    initialValues: useCallback(
      () => ({
        estadoCivil: null,
        uniaoEstavel: false,
        ...comprador,
        conjuge: {
          nome: "",
          cpfCnpj: "",
          dataNascimento: "",
          telefone: "",
          nacionalidade: null,
          compoeRenda: false,
          ...comprador?.conjuge,
        },
      }),
      [comprador]
    ),
    handleSubmit: useCallback(
      (values) => {
        dispatch(
          actions.gravarPreenchimento.request({
            callback,
            instituicao: operacao.codigoInstituicao,
            passo: "dados-estado-civil",
            participante: {
              id: comprador?.id,
              uniaoEstavel: exibeUniaoEstavel && values.uniaoEstavel,
              idEstadoCivil: values.estadoCivil?.id,
              conjuge: !!exibeConjuge
                ? {
                    nome: values.conjuge.nome,
                    cpfCnpj: removeNonDigitsFromString(values.conjuge.cpfCnpj),
                    dataNascimento: transformToDate(
                      values.conjuge.dataNascimento
                    ),
                    telefone: values.conjuge.telefone,
                    idNacionalidade: values.conjuge.nacionalidade?.id,
                    compoeRenda: values.conjuge.compoeRenda,
                  }
                : null,
            },
            operacao: {
              id: operacao.id,
            },
          })
        );
      },
      [dispatch, comprador, operacao, exibeConjuge, exibeUniaoEstavel, callback]
    ),
  });
  const { setValue } = formProps;

  const handleEstadoCivilChange = useCallback(
    (v) => {
      const vaiMudar =
        !exibeConjuge && !exibeUniaoEstavel
          ? true
          : exibeUniaoEstavel ^ (v?.uniaoEstavel ?? false);

      if (vaiMudar) {
        setValue("uniaoEstavel", false);
        setExibeUniaoEstavel(v?.uniaoEstavel ?? false);
        setExibeConjuge(v?.conjuge ?? false);
      }
    },
    [
      setExibeConjuge,
      setExibeUniaoEstavel,
      exibeUniaoEstavel,
      exibeConjuge,
      setValue,
    ]
  );
  const handleUniaoEstavelChange = useCallback(
    (e) => {
      setExibeConjuge(e.target.checked);
    },
    [setExibeConjuge]
  );

  const estadosCivisFiltrados =
    !!comprador?.principal || isSegundoCompradorConjuge
      ? estadosCivis
      : estadosCivis.filter((e) => !e.conjuge);

  return (
    <DadosEstadoCivilDefaultForm
      {...props}
      estadosCivis={estadosCivisFiltrados}
      nacionalidades={nacionalidades}
      events={events}
      principal={comprador?.principal}
      fluxo={comprador.fluxo}
      isSegundoCompradorConjuge={isSegundoCompradorConjuge}
      formProps={formProps}
      handleEstadoCivilChange={handleEstadoCivilChange}
      handleSubmit={handleSubmit}
      exibeConjuge={exibeConjuge}
      exibeUniaoEstavel={exibeUniaoEstavel}
      handleUniaoEstavelChange={handleUniaoEstavelChange}
      submitting={submitting}
    />
  );
};

export default DadosEstadoCivilDefaultFormConnected;
